import { FIRST_NAMES } from '@/data/firstNames'
import { LAST_NAMES } from '@/data/lastNames'
import type { RecruitmentSource } from '@/types'

// Get random element from array
function randomElement<T>(array: T[]): T {
  return array[Math.floor(Math.random() * array.length)]
}

// Generate a random full name
export function generateRandomName(): string {
  const firstName = randomElement(FIRST_NAMES)
  const lastName = randomElement(LAST_NAMES)
  return `${firstName} ${lastName}`
}

// Generate multiple unique names
export function generateUniqueNames(count: number): string[] {
  const names = new Set<string>()
  const maxAttempts = count * 10 // Prevent infinite loop
  let attempts = 0

  while (names.size < count && attempts < maxAttempts) {
    names.add(generateRandomName())
    attempts++
  }

  return Array.from(names)
}

// Check if a name already exists (for duplicate prevention)
export function isNameUnique(name: string, existingNames: string[]): boolean {
  return !existingNames.includes(name)
}

// Generate a name that's not in the existing list
export function generateUniqueName(existingNames: string[]): string {
  const maxAttempts = 100
  let attempts = 0

  while (attempts < maxAttempts) {
    const name = generateRandomName()
    if (isNameUnique(name, existingNames)) {
      return name
    }
    attempts++
  }

  // Fallback: add a number suffix
  const baseName = generateRandomName()
  return `${baseName} Jr.`
}

// Age ranges by recruitment source
const AGE_RANGES: Record<RecruitmentSource, { min: number; max: number; median: number }> = {
  youth_wing: { min: 18, max: 32, median: 25 },
  union: { min: 28, max: 58, median: 42 },
  business: { min: 32, max: 62, median: 48 },
  activist: { min: 22, max: 45, median: 32 },
  think_tank: { min: 30, max: 55, median: 42 },
  independent: { min: 35, max: 65, median: 50 },
  poached: { min: 30, max: 60, median: 45 },
}

// Generate age based on recruitment source (normal-ish distribution around median)
export function generateAge(source: RecruitmentSource): number {
  const range = AGE_RANGES[source]

  // Simple triangular distribution toward median
  // Average of two random numbers tends toward 0.5
  const r1 = Math.random()
  const r2 = Math.random()
  const avg = (r1 + r2) / 2

  // Map to age range with bias toward median
  const age = Math.round(range.min + avg * (range.max - range.min))

  return Math.max(range.min, Math.min(range.max, age))
}

// Get age range info for a recruitment source (useful for UI)
export function getAgeRangeForSource(
  source: RecruitmentSource
): { min: number; max: number; median: number } {
  return AGE_RANGES[source]
}
